/*
 *   This content is licensed according to the W3C Software License at
 *   https://www.w3.org/Consortium/Legal/2015/copyright-software-and-document
 *
 *   File:   DatePickerDialog.js
 */

'use strict';

import { getAllMonthNames } from '../../utils/index';

// ToDo: Move everything that has with input out of this class

class DatePickerDialog {
    constructor(cdp, options = {}) {
        const {
            format,
            minDate,
            maxDate,
            mondayFirstDayOfTheWeek,
            dateRange = false,
            localization = {},
            defaultDate = null,
            elementId,
        } = options;

        this.mondayFirstDayOfTheWeek = mondayFirstDayOfTheWeek;

        const langMap = {
            sv: 'sv-SE',
            en: 'en-US',
        };

        this.lang =
            langMap[document.documentElement.getAttribute('lang')] || 'en-US';

        this.isDateRange = dateRange;

        if (format) {
            this.format = format;
        } else {
            this.format = 'DD/MM/YYYY';
        }

        if (minDate) {
            this.minDate = new Date(Date.parse(minDate));
            this.minDate.setHours(0, 0, 0, 0);
        }

        if (maxDate) {
            this.maxDate = new Date(Date.parse(maxDate));
            this.maxDate.setHours(0, 0, 0, 0);
        }

        this.date = null;
        this.hoverDate = null;

        this.months = getAllMonthNames({ locale: this.lang });

        // Localization
        this.buttonLabelChoose = localization.chooseDate || 'Choose Date';
        this.buttonLabelChange = localization.changeDate || 'Change Date';

        this.input = cdp.querySelector(`input#${elementId}-input`);

        this.buttonNode = cdp.querySelector('.group button.icon');
        this.dialogNode = cdp.querySelector('[role="dialog"]');
        this.messageNode = this.dialogNode.querySelector('.dialog-message');

        const calendarsElement = document.querySelector('.calendars');

        this.isDoubleCalendars =
            calendarsElement && calendarsElement.children.length === 2;

        const calendar = this.dialogNode.querySelector('.calendar');

        if (calendar) {
            this.calendar = {
                focusDay: new Date(),
                days: [],
                monthYearNode: calendar.querySelector('.month-year'),
                prevYearNode: calendar.querySelector('.prev-year'),
                prevMonthNode: calendar.querySelector('.prev-month'),
                nextMonthNode: calendar.querySelector('.next-month'),
                nextYearNode: calendar.querySelector('.next-year'),
                tbodyNode: calendar.querySelector('table.dates tbody'),
                lastRowNode: null,
                selectedDay: new Date(0, 0, 1),
            };

            this.bindButtons(this.calendar);
        }

        this.okButtonNode = this.dialogNode.querySelector('button[value="ok"]');
        this.cancelButtonNode = this.dialogNode.querySelector(
            'button[value="cancel"]'
        );

        this.liveRegion = this.dialogNode.querySelector('#dialog-live-region');

        this.lastDate = -1;

        this.isMouseDownOnBackground = false;

        if (defaultDate) {
            this.defaultDate = new Date(Date.parse(defaultDate));
            this.setDate(defaultDate);
        }
        this.input.addEventListener('blur', (e) =>
            this.setDateForButtonLabel(e)
        );

        this.buttonNode.addEventListener('keydown', (e) =>
            this.handleButtonKeydown(e)
        );
        this.buttonNode.addEventListener('click', (e) =>
            this.handleButtonClick(e)
        );

        if (this.okButtonNode) {
            this.okButtonNode.addEventListener('click', (e) =>
                this.handleOkButton(e)
            );
            this.okButtonNode.addEventListener('keydown', (e) =>
                this.handleOkButton(e)
            );
        }

        if (this.cancelButtonNode) {
            this.cancelButtonNode.addEventListener('click', (e) =>
                this.handleCancelButton(e)
            );
            this.cancelButtonNode.addEventListener('keydown', (e) =>
                this.handleCancelButton(e)
            );
        }

        document.body.addEventListener(
            'pointerup',
            (e) => this.handleBackgroundMouseUp(e),
            true
        );

        this.createGridDays(this.calendar);
        this.updateGrid(this.calendar);

        this.close(false);
        this.setDateForButtonLabel();
    }

    setOptions(options = {}) {
        let shouldRedraw = false;

        if (options.minDate !== undefined) {
            this.minDate = new Date(Date.parse(options.minDate));
            this.minDate.setHours(0, 0, 0, 0);
            shouldRedraw = true;
        }

        if (options.maxDate !== undefined) {
            this.maxDate = new Date(Date.parse(options.maxDate));
            this.maxDate.setHours(0, 0, 0, 0);
            shouldRedraw = true;
        }

        if (shouldRedraw) {
            this.updateGrid(this.calendar);
        }
    }

    createGridDays(calendar) {
        calendar.tbodyNode.innerHTML = '';
        for (let i = 0; i < 6; i++) {
            const row = calendar.tbodyNode.insertRow(i);
            calendar.lastRowNode = row;
            for (let j = 0; j < 7; j++) {
                const cell = document.createElement('td');

                cell.tabIndex = -1;
                cell.addEventListener('click', (e) =>
                    this.handleDayClick(e, calendar)
                );
                cell.addEventListener('keydown', (e) =>
                    this.handleDayKeyDown(e, calendar)
                );

                cell.textContent = '-1';

                cell.setAttribute('aria-colindex', (j + 1).toString());

                row.appendChild(cell);
                calendar.days.push(cell);
            }
        }
    }

    bindButtons(calendar) {
        calendar.prevMonthNode.addEventListener('click', (e) =>
            this.handlePreviousMonthButton(e, calendar)
        );
        calendar.nextMonthNode.addEventListener('click', (e) =>
            this.handleNextMonthButton(e, calendar)
        );
        calendar.prevYearNode.addEventListener('click', (e) =>
            this.handlePreviousYearButton(e, calendar)
        );
        calendar.nextYearNode.addEventListener('click', (e) =>
            this.handleNextYearButton(e, calendar)
        );

        calendar.prevMonthNode.addEventListener('keydown', (e) =>
            this.handlePreviousMonthButton(e, calendar)
        );
        calendar.nextMonthNode.addEventListener('keydown', (e) =>
            this.handleNextMonthButton(e, calendar)
        );
        calendar.prevYearNode.addEventListener('keydown', (e) =>
            this.handlePreviousYearButton(e, calendar)
        );
        calendar.nextYearNode.addEventListener('keydown', (e) =>
            this.handleNextYearButton(e, calendar)
        );
    }

    getLocalizedDayName(date, options) {
        const dayName = date.toLocaleString(this.lang, options);
        return dayName.charAt(0).toUpperCase() + dayName.slice(1);
    }

    getLocalizedMonthName(date) {
        const monthName = date.toLocaleString(this.lang, { month: 'long' });
        return monthName.charAt(0).toUpperCase() + monthName.slice(1);
    }

    getOrdinal(date) {
        const day = date.getDate();

        if (this.lang === 'sv-SE') {
            switch (day % 10) {
                case 1:
                case 2:
                    return ':a';
                default:
                    return ':e';
            }
        }

        switch (day % 10) {
            case 1:
                return 'st';
            case 2:
                return 'nd';
            case 3:
                return 'rd';
            default:
                return 'th';
        }
    }

    isSameDay(day1, day2) {
        return (
            day1.getFullYear() == day2.getFullYear() &&
            day1.getMonth() == day2.getMonth() &&
            day1.getDate() == day2.getDate()
        );
    }

    isNotSameMonth(day1, day2) {
        return (
            day1.getFullYear() != day2.getFullYear() ||
            day1.getMonth() != day2.getMonth()
        );
    }

    updateButtons(calendar) {
        const d = calendar.focusDay;

        const disablePrevMonth = !this.isLastDayOfMonthInValidRange(
            new Date(d.getFullYear(), d.getMonth() - 1)
        );
        const disablePrevYear = !this.isLastDayOfMonthInValidRange(
            new Date(d.getFullYear() - 1, d.getMonth())
        );
        const disableNextMonth = !this.isFirstDayOfMonthInValidRange(
            new Date(d.getFullYear(), d.getMonth() + 1)
        );
        const disableNextYear = !this.isFirstDayOfMonthInValidRange(
            new Date(d.getFullYear() + 1, d.getMonth())
        );

        // Would like to do .focus() Date here. but setFocusDay comes after
        // Could break out code below and call a new function from event handlers for these buttons?

        if (
            disablePrevMonth &&
            calendar.prevMonthNode === document.activeElement
        ) {
            const nextEl = this.getNextInteractiveElement(
                calendar.prevMonthNode
            );
            if (nextEl) nextEl.focus();
        }
        if (
            disablePrevYear &&
            calendar.prevYearNode === document.activeElement
        ) {
            const nextEl = this.getNextInteractiveElement(
                calendar.prevYearNode
            );
            if (nextEl) nextEl.focus();
        }
        if (
            disableNextMonth &&
            calendar.nextMonthNode === document.activeElement
        ) {
            const prevEl = this.getPreviousInterativeElement(
                calendar.nextMonthNode
            );
            if (prevEl) prevEl.focus();
        }
        if (
            disableNextYear &&
            calendar.nextYearNode === document.activeElement
        ) {
            const prevEl = this.getPreviousInterativeElement(
                calendar.nextYearNode
            );
            if (prevEl) prevEl.focus();
        }

        calendar.prevMonthNode.disabled = disablePrevMonth;
        calendar.prevYearNode.disabled = disablePrevYear;
        calendar.nextMonthNode.disabled = disableNextMonth;
        calendar.nextYearNode.disabled = disableNextYear;
    }

    updateGrid(calendar) {
        const fd = calendar.focusDay;
        calendar.monthYearNode.textContent = `${this.getLocalizedMonthName(
            fd
        )} ${fd.getFullYear()}`;

        let firstDayOfMonth = new Date(fd.getFullYear(), fd.getMonth(), 1);
        let dayOfWeek = firstDayOfMonth.getDay();

        if (this.mondayFirstDayOfTheWeek) {
            dayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        }

        firstDayOfMonth.setDate(firstDayOfMonth.getDate() - dayOfWeek);

        const d = new Date(firstDayOfMonth);

        for (let i = 0; i < calendar.days.length; i++) {
            const flag =
                (Boolean(this.minDate) &&
                    Date.parse(d.toISOString()) < Date.parse(this.minDate)) ||
                (Boolean(this.maxDate) &&
                    Date.parse(d.toISOString()) > Date.parse(this.maxDate));
            const notCurrentMonth = d.getMonth() != fd.getMonth();
            this.updateDate(
                calendar.days[i],
                flag,
                d,
                this.isSameDay(d, calendar.selectedDay),
                notCurrentMonth
            );

            // Hide last row if all dates are disabled (e.g. in next month)
            if (i === 35) {
                if (d.getMonth() !== fd.getMonth()) {
                    if (!calendar.lastRowNode.classList.contains('hidden'))
                        calendar.lastRowNode.classList.add('hidden');
                } else {
                    calendar.lastRowNode.classList.remove('hidden');
                }
            }

            d.setDate(d.getDate() + 1);
        }

        this.updateButtons(calendar);
    }

    updateDate(domNode, disable, day, selected, notCurrentMonth) {
        let d = day.getDate().toString();
        if (day.getDate() <= 9) {
            d = '0' + d;
        }

        let m = day.getMonth() + 1;
        if (day.getMonth() < 9) {
            m = '0' + m;
        }

        domNode.tabIndex = -1;
        domNode.removeAttribute('aria-selected');
        domNode.setAttribute(
            'data-date',
            day.getFullYear() + '-' + m + '-' + d
        );
        domNode.textContent = day.getDate();

        if (disable) {
            domNode.classList.add('disabled');
        } else {
            domNode.classList.remove('disabled');
            if (selected) {
                domNode.setAttribute('aria-selected', 'true');

                if (this.isActiveCalendar(this.calendar)) {
                    domNode.tabIndex = 0;
                }
            }
        }

        if (notCurrentMonth) {
            domNode.classList.add('not-current-month');
            domNode.setAttribute('aria-hidden', 'true');
        } else {
            domNode.classList.remove('not-current-month');
            domNode.removeAttribute('aria-hidden');
        }
    }

    moveFocusToDay(day, calendar) {
        const d = calendar.focusDay;

        calendar.focusDay = day;
        this.hoverDate = day;

        if (
            d.getMonth() != calendar.focusDay.getMonth() ||
            d.getFullYear() != calendar.focusDay.getFullYear()
        ) {
            this.updateGrid(calendar);
            this.updateDateSelection(calendar);
        }
        this.setFocusDay(calendar);
    }

    setFocusDay(calendar, flag) {
        if (typeof flag !== 'boolean') {
            flag = true;
        }

        for (let i = 0; i < calendar.days.length; i++) {
            const dayNode = calendar.days[i];
            const day = this.getDayFromDataDateAttribute(dayNode);

            dayNode.tabIndex = -1;
            if (this.isSameDay(day, calendar.focusDay)) {
                if (this.isActiveCalendar(calendar)) {
                    dayNode.tabIndex = 0;
                    if (flag) {
                        dayNode.focus();
                    }
                }
            }
        }
    }

    open() {
        this.dialogNode.style.display = 'block';
        this.dialogNode.style.zIndex = 10;

        this.getDateFromTextbox(this.input);

        this.updateGrid(this.calendar);

        this.updateDateSelection(this.calendar);

        this.lastDate = this.calendar.focusDay.getDate();

        const announcement = 'Select a date from the calendar';
        this.updateLiveRegion(announcement);
    }

    isOpen() {
        return window.getComputedStyle(this.dialogNode).display !== 'none';
    }

    close(flag) {
        if (typeof flag !== 'boolean') {
            flag = true;
        }

        this.dialogNode.style.display = 'none';

        if (flag) {
            this.buttonNode.focus();
        }
    }

    changeMonth(calendar, numMonths) {
        const currentDate = calendar.focusDay;
        const getDays = (year, month) => new Date(year, month, 0).getDate();

        const isPrev = numMonths < 0;
        const numYears = Math.trunc(Math.abs(numMonths) / 12);
        numMonths = Math.abs(numMonths) % 12;

        const newYear = isPrev
            ? currentDate.getFullYear() - numYears
            : currentDate.getFullYear() + numYears;

        const newMonth = isPrev
            ? currentDate.getMonth() - numMonths
            : currentDate.getMonth() + numMonths;

        const newDate = new Date(newYear, newMonth, 1);

        const daysInMonth = getDays(
            newDate.getFullYear(),
            newDate.getMonth() + 1
        );

        calendar.lastDate = calendar.lastDate
            ? calendar.lastDate
            : currentDate.getDate();

        if (calendar.lastDate > daysInMonth) {
            newDate.setDate(daysInMonth);
        } else {
            newDate.setDate(calendar.lastDate);
        }

        return newDate;
    }

    moveToNextYear(calendar) {
        const d = new Date(
            calendar.focusDay.getFullYear(),
            calendar.focusDay.getMonth() + 12,
            1
        );

        if (!this.isDateInValidRange(d)) return;

        calendar.focusDay = this.changeMonth(calendar, 12);
        this.updateGrid(calendar);
        this.updateDateSelection(calendar);
    }

    moveToPreviousYear(calendar) {
        const d = new Date(
            calendar.focusDay.getFullYear(),
            calendar.focusDay.getMonth() - 11,
            0
        );

        if (!this.isDateInValidRange(d)) return;

        calendar.focusDay = this.changeMonth(calendar, -12);
        this.updateGrid(calendar);
        this.updateDateSelection(calendar);
    }

    moveToNextMonth(calendar) {
        const d = new Date(
            calendar.focusDay.getFullYear(),
            calendar.focusDay.getMonth() + 1,
            1
        );

        if (!this.isDateInValidRange(d)) return;

        calendar.focusDay = this.changeMonth(calendar, 1);
        this.updateGrid(calendar);
        this.updateDateSelection(calendar);
    }

    moveToPreviousMonth(calendar) {
        const d = new Date(
            calendar.focusDay.getFullYear(),
            calendar.focusDay.getMonth(),
            0
        );

        if (!this.isDateInValidRange(d)) return;

        calendar.focusDay = this.changeMonth(calendar, -1);
        this.updateGrid(calendar);
        this.updateDateSelection(calendar);
    }

    moveFocusToNextDay(calendar) {
        const d = new Date(calendar.focusDay);
        d.setDate(d.getDate() + 1);

        if (!this.isDateInValidRange(d)) return;

        this.lastDate = d.getDate();
        this.moveFocusToDay(d, calendar);
    }

    moveFocusToNextWeek(calendar) {
        const d = new Date(calendar.focusDay);
        d.setDate(d.getDate() + 7);

        if (!this.isDateInValidRange(d)) return;

        this.lastDate = d.getDate();
        this.moveFocusToDay(d, calendar);
    }

    moveFocusToPreviousDay(calendar) {
        const d = new Date(calendar.focusDay);
        d.setDate(d.getDate() - 1);

        if (!this.isDateInValidRange(d)) return;

        this.lastDate = d.getDate();
        this.moveFocusToDay(d, calendar);
    }

    moveFocusToPreviousWeek(calendar) {
        const d = new Date(calendar.focusDay);
        d.setDate(d.getDate() - 7);

        if (!this.isDateInValidRange(d)) return;

        this.lastDate = d.getDate();
        this.moveFocusToDay(d, calendar);
    }

    moveFocusToFirstDayOfWeek(calendar) {
        const d = new Date(calendar.focusDay);

        if (this.mondayFirstDayOfTheWeek) {
            const daysToMoveBack = d.getDay() === 0 ? 6 : d.getDay() - 1;
            d.setDate(d.getDate() - daysToMoveBack);
        } else {
            d.setDate(d.getDate() - d.getDay());
        }

        if (!this.isDateInValidRange(d)) return;

        this.lastDate = d.getDate();
        this.moveFocusToDay(d, calendar);
    }

    moveFocusToLastDayOfWeek(calendar) {
        const d = new Date(calendar.focusDay);

        if (this.mondayFirstDayOfTheWeek) {
            const daysToMoveForward = d.getDay() === 0 ? 0 : 7 - d.getDay();
            d.setDate(d.getDate() + daysToMoveForward);
        } else {
            d.setDate(d.getDate() + (6 - d.getDay()));
        }

        if (!this.isDateInValidRange(d)) return;

        this.lastDate = d.getDate();
        this.moveFocusToDay(d, calendar);
    }

    isDayDisabled(domNode) {
        if (!domNode) return false;

        return domNode.classList.contains('disabled');
    }

    isDayNotCurrentMonth(domNode) {
        if (!domNode) return false;

        return domNode.classList.contains('not-current-month');
    }

    isDateInValidRange(date) {
        if (this.minDate && date < this.minDate) return false;
        if (this.maxDate && date > this.maxDate) return false;
        return true;
    }

    isLastDayOfMonthInValidRange(date) {
        const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        return this.isDateInValidRange(lastDay);
    }

    isFirstDayOfMonthInValidRange(date) {
        const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
        return this.isDateInValidRange(firstDay);
    }

    getDayFromDataDateAttribute(domNode) {
        const parts = domNode.getAttribute('data-date').split('-');
        return new Date(parts[0], parseInt(parts[1]) - 1, parts[2]);
    }

    extractDateComponents(dateString) {
        if (dateString === undefined || dateString.trim() === '') {
            return { year: undefined, month: undefined, day: undefined };
        }

        const formatParts = this.format.split(/[\s-/]/);
        const dateParts = dateString.split(/[\s-/]/);

        let year, month, day;

        formatParts.forEach((part, index) => {
            if (
                part === undefined ||
                dateParts[index] === undefined ||
                dateParts[index].trim() === ''
            )
                return;

            switch (part) {
                case 'YYYY':
                    year = parseInt(dateParts[index], 10);
                    break;
                case 'MM':
                    month = parseInt(dateParts[index], 10);
                    break;
                case 'D':
                case 'DD':
                    day = parseInt(dateParts[index], 10);
                    break;
                case 'MMMM': {
                    const monthIndex = this.months.findIndex(
                        (m) =>
                            m.toLowerCase() === dateParts[index].toLowerCase()
                    );
                    month = monthIndex >= 0 ? monthIndex + 1 : undefined;
                    break;
                }
            }
        });

        return { year, month, day };
    }

    formatDate(date, format) {
        const map = {
            YYYY: date.getFullYear(),
            MM: String(date.getMonth() + 1).padStart(2, '0'),
            MMMM:
                this.months[date.getMonth()][0].toUpperCase() +
                this.months[date.getMonth()].slice(1),
            D: String(date.getDate()),
            DD: String(date.getDate()).padStart(2, '0'),
            HH: String(date.getHours()).padStart(2, '0'),
            mm: String(date.getMinutes()).padStart(2, '0'),
            ss: String(date.getSeconds()).padStart(2, '0'),
        };

        return format.replace(
            /YYYY|MMMM|MM|DD|D|HH|mm|ss/g,
            (matched) => map[matched]
        );
    }

    setInputDate(calendar, domNode) {
        let d = calendar.focusDay;

        if (domNode) {
            d = this.getDayFromDataDateAttribute(domNode);

            this.date = new Date(d);
            this.input.value = this.formatDate(this.date, this.format);
            this.input.classList.add('has-value');
            this.dispatchChange(this.input);
            this.setFocusDay(this.calendar, false);

            this.updateDateSelection(this.calendar);
        }

        this.setDateForButtonLabel();
    }

    getDateFromTextbox(domNode) {
        const selectedDate = this.extractDateComponents(domNode.value);
        const { month, day } = selectedDate;
        let { year } = selectedDate;

        if (
            Number.isInteger(month) &&
            Number.isInteger(day) &&
            Number.isInteger(year)
        ) {
            if (year < 100) {
                year = 2000 + year;
            }

            this.calendar.focusDay = new Date(year, month - 1, day);
            this.calendar.selectedDay = new Date(this.focusDay);
        } else {
            this.calendar.focusDay = new Date();
            this.calendar.selectedDay = new Date(0, 0, 1);
        }
    }

    setDateForButtonLabel() {
        const selectedDate = this.extractDateComponents(this.input.value);

        if (
            Number.isInteger(parseInt(selectedDate.month)) &&
            Number.isInteger(parseInt(selectedDate.day)) &&
            Number.isInteger(parseInt(selectedDate.year))
        ) {
            const day = new Date(
                parseInt(selectedDate.year),
                parseInt(selectedDate.month) - 1,
                parseInt(selectedDate.day)
            );

            const label =
                this.buttonLabelChange +
                ', ' +
                this.formatDateToLocalizedString(day);
            this.buttonNode.setAttribute('aria-label', label);
        } else {
            this.buttonNode.setAttribute('aria-label', this.buttonLabelChoose);
        }
    }

    handleOkButton(event) {
        let flag = false;

        switch (event.type) {
            case 'keydown':
                switch (event.key) {
                    case 'Esc':
                    case 'Escape':
                        this.close();
                        flag = true;
                        break;

                    default:
                        break;
                }
                break;

            case 'click':
                this.setInputDate(this.calendar);
                this.close();
                flag = true;
                break;

            default:
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    handleCancelButton(event) {
        let flag = false;

        switch (event.type) {
            case 'keydown':
                switch (event.key) {
                    case 'Tab':
                        if (!event.shiftKey) {
                            const nextInteractiveElement =
                                this.getNextInteractiveElement(
                                    event.currentTarget
                                );
                            if (nextInteractiveElement)
                                nextInteractiveElement.focus();

                            flag = true;
                        }
                        break;

                    case 'Esc':
                    case 'Escape':
                        this.close();
                        flag = true;
                        break;

                    default:
                        break;
                }
                break;

            case 'click':
                this.close();
                flag = true;
                break;

            default:
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    handleNextYearButton(event, calendar) {
        let flag = false;

        switch (event.type) {
            case 'keydown':
                switch (event.key) {
                    case 'Esc':
                    case 'Escape':
                        this.close();
                        flag = true;
                        break;

                    case 'Enter':
                        this.moveToNextYear(calendar);
                        this.setFocusDay(calendar, false);
                        flag = true;
                        break;

                    case 'Tab':
                        if (event.shiftKey) {
                            const previousInteractiveElement =
                                this.getPreviousInterativeElement(
                                    event.currentTarget
                                );
                            if (previousInteractiveElement)
                                previousInteractiveElement.focus();

                            flag = true;
                        }
                        break;
                }

                break;

            case 'click':
                this.moveToNextYear(calendar);
                this.setFocusDay(calendar, false);
                break;

            default:
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    handlePreviousYearButton(event, calendar) {
        let flag = false;

        switch (event.type) {
            case 'keydown':
                switch (event.key) {
                    case 'Enter':
                        this.moveToPreviousYear(calendar);
                        this.setFocusDay(calendar, false);
                        flag = true;
                        break;

                    case 'Tab':
                        if (event.shiftKey) {
                            const previousInteractiveElement =
                                this.getPreviousInterativeElement(
                                    event.currentTarget
                                );
                            if (previousInteractiveElement)
                                previousInteractiveElement.focus();

                            flag = true;
                        }
                        break;

                    case 'Esc':
                    case 'Escape':
                        this.close();
                        flag = true;
                        break;

                    default:
                        break;
                }

                break;

            case 'click':
                this.moveToPreviousYear(calendar);
                this.setFocusDay(calendar, false);
                break;

            default:
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    handleNextMonthButton(event, calendar) {
        let flag = false;

        switch (event.type) {
            case 'keydown':
                switch (event.key) {
                    case 'Esc':
                    case 'Escape':
                        this.close();
                        flag = true;
                        break;

                    case 'Enter':
                        this.moveToNextMonth(calendar);
                        this.setFocusDay(calendar, false);
                        flag = true;
                        break;

                    case 'Tab':
                        if (event.shiftKey) {
                            const previousInteractiveElement =
                                this.getPreviousInterativeElement(
                                    event.currentTarget
                                );
                            if (previousInteractiveElement)
                                previousInteractiveElement.focus();

                            flag = true;
                        }
                        break;
                }

                break;

            case 'click':
                this.moveToNextMonth(calendar);
                this.setFocusDay(calendar, false);
                break;

            default:
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    handlePreviousMonthButton(event, calendar) {
        let flag = false;

        switch (event.type) {
            case 'keydown':
                switch (event.key) {
                    case 'Esc':
                    case 'Escape':
                        this.close();
                        flag = true;
                        break;

                    case 'Enter':
                        this.moveToPreviousMonth(calendar);
                        this.setFocusDay(calendar, false);
                        flag = true;
                        break;

                    case 'Tab':
                        if (event.shiftKey) {
                            const previousInteractiveElement =
                                this.getPreviousInterativeElement(
                                    event.currentTarget
                                );
                            if (previousInteractiveElement)
                                previousInteractiveElement.focus();

                            flag = true;
                        }
                        break;
                }

                break;

            case 'click':
                this.moveToPreviousMonth(calendar);
                this.setFocusDay(calendar, false);
                flag = true;
                break;

            default:
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    handleDayKeyDown(event, calendar) {
        let flag = false;

        switch (event.key) {
            case 'Esc':
            case 'Escape':
                this.close();
                flag = true;
                break;

            case ' ':
                this.setInputDate(calendar, event.currentTarget);
                this.close();
                flag = true;
                break;

            case 'Enter':
                if (this.isDayDisabled(event.currentTarget)) {
                    break;
                }

                this.setInputDate(calendar, event.currentTarget);

                this.close();
                flag = true;
                break;

            case 'Tab':
                if (event.shiftKey) {
                    const prevEl = this.getPreviousInterativeElement(
                        event.currentTarget
                    );

                    if (prevEl) prevEl.focus();
                    flag = true;

                    break;
                }

                this.okButtonNode.focus();

                flag = true;
                break;

            case 'Right':
            case 'ArrowRight':
                this.moveFocusToNextDay(calendar);
                flag = true;
                break;

            case 'Left':
            case 'ArrowLeft':
                this.moveFocusToPreviousDay(calendar);
                flag = true;
                break;

            case 'Down':
            case 'ArrowDown':
                this.moveFocusToNextWeek(calendar);
                flag = true;
                break;

            case 'Up':
            case 'ArrowUp':
                this.moveFocusToPreviousWeek(calendar);
                flag = true;
                break;

            case 'PageUp':
                if (event.shiftKey) {
                    this.moveToPreviousYear(calendar);
                } else {
                    this.moveToPreviousMonth(calendar);
                }
                this.setFocusDay(calendar);
                flag = true;
                break;

            case 'PageDown':
                if (event.shiftKey) {
                    this.moveToNextYear(calendar);
                } else {
                    this.moveToNextMonth(calendar);
                }
                this.setFocusDay(calendar);
                flag = true;
                break;

            case 'Home':
                this.moveFocusToFirstDayOfWeek(calendar);
                flag = true;
                break;

            case 'End':
                this.moveFocusToLastDayOfWeek(calendar);
                flag = true;
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    handleDayClick(event, calendar) {
        if (
            !this.isDayDisabled(event.currentTarget) &&
            !this.isDayNotCurrentMonth(event.currentTarget) &&
            event.which !== 3
        ) {
            this.setInputDate(calendar, event.currentTarget);
            this.close();
        }

        event.stopPropagation();
        event.preventDefault();
    }

    handleButtonKeydown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
            this.open();
            this.setFocusDay(this.calendar);

            event.stopPropagation();
            event.preventDefault();
        }
    }

    handleButtonClick(event) {
        if (this.isOpen()) {
            this.close();
        } else {
            this.open();
            this.setFocusDay(this.calendar);
        }

        event.stopPropagation();
        event.preventDefault();
    }

    handleBackgroundMouseUp(event) {
        if (
            !this.buttonNode.contains(event.target) &&
            !this.dialogNode.contains(event.target)
        ) {
            if (this.isOpen()) {
                this.close(false);
                event.stopPropagation();
                event.preventDefault();
            }
        }
    }

    updateDateSelection(calendar) {
        calendar.days.forEach((dayNode) => {
            const day = this.getDayFromDataDateAttribute(dayNode);
            dayNode.classList.remove(
                'selected',
                'selected-start',
                'selected-end',
                'in-range',
                'hover-range',
                'hover-end'
            );
            dayNode.removeAttribute('aria-selected');
            dayNode.removeAttribute('aria-label');

            let ariaLabel = this.formatDateToLocalizedString(day);

            if (this.date && this.isSameDay(day, this.date)) {
                dayNode.classList.add('selected');
                dayNode.setAttribute('aria-selected', 'true');

                if (this.localization?.ariaLabelSelectedDate) {
                    ariaLabel = this.localization.ariaLabelSelectedDate.replace(
                        '{date}',
                        this.formatDateToLocalizedString(day)
                    );
                } else {
                    ariaLabel += ', selected date';
                }
            }

            dayNode.setAttribute('aria-label', ariaLabel);
        });

        if (this.date) {
            if (this.localization?.announcementDateSelected) {
                this.updateLiveRegion(
                    this.localization.announcementDateSelected.replace(
                        '{date}',
                        this.formatDateToLocalizedString(this.date)
                    )
                );
                return;
            } else {
                this.updateLiveRegion(
                    `Date selected: ${this.formatDate(this.date, this.format)}`
                );
            }
        }
    }

    updateLiveRegion(text) {
        if (!this.liveRegion) return;

        this.liveRegion.textContent = text;
    }

    dispatchChange(domNode) {
        const event = new CustomEvent('datepicker-dialog:change', {
            bubbles: true,
            cancelable: true,
            detail: {},
        });

        domNode.dispatchEvent(event);
    }

    isActiveCalendar(calendar) {
        return calendar === this.getActiveCalendar();
    }

    getDomNode(date) {
        const dateStr = this.formatDate(date, this.format);
        const domNode = document.querySelector(`[data-date="${dateStr}"]`);

        return domNode;
    }

    getActiveCalendar() {
        return this.calendar;
    }

    getInteractiveDayElement(calendar) {
        return calendar.days.find((day) => day.tabIndex === 0);
    }

    getAllInteractiveElements() {
        const activeCalendar = this.getActiveCalendar();
        const interactiveElements = [];

        if (!activeCalendar.prevYearNode.disabled)
            interactiveElements.push(activeCalendar.prevYearNode);
        if (!activeCalendar.prevMonthNode.disabled)
            interactiveElements.push(activeCalendar.prevMonthNode);
        if (!activeCalendar.nextMonthNode.disabled)
            interactiveElements.push(activeCalendar.nextMonthNode);
        if (!activeCalendar.nextYearNode.disabled)
            interactiveElements.push(activeCalendar.nextYearNode);

        const activeCalendarDay = this.getInteractiveDayElement(activeCalendar);
        if (activeCalendarDay) interactiveElements.push(activeCalendarDay);

        if (this.okButtonNode) interactiveElements.push(this.okButtonNode);
        if (this.cancelButtonNode)
            interactiveElements.push(this.cancelButtonNode);

        return interactiveElements;
    }

    getPreviousInterativeElement(domNode) {
        const interactiveElements = this.getAllInteractiveElements();
        const activeIndex = interactiveElements.findIndex(
            (node) => node === domNode
        );

        if (activeIndex === 0) return interactiveElements.pop();

        return interactiveElements[activeIndex - 1];
    }

    getNextInteractiveElement(domNode) {
        const interactiveElements = this.getAllInteractiveElements();
        const activeIndex = interactiveElements.findIndex(
            (node) => node === domNode
        );

        if (activeIndex + 1 === interactiveElements.length)
            return interactiveElements.shift();

        return interactiveElements[activeIndex + 1];
    }

    formatDateToLocalizedString(date) {
        const options = {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        };

        return date.toLocaleDateString(this.lang, options);
    }

    setDate(date) {
        this.date =
            typeof date === 'string' ? new Date(Date.parse(date)) : date;
        this.input.value = this.formatDate(this.date, this.format);
        this.input.classList.add('has-value');
        this.calendar.focusDay = this.date;
        this.dispatchChange(this.input);
        this.updateGrid(this.calendar);
        this.updateDateSelection(this.calendar);
        this.setFocusDay(this.calendar, false);
    }

    clearDate() {
        this.date = null;
        this.input.value = '';
        this.input.classList.remove('has-value');
        this.calendar.focusDay = new Date();
        this.updateGrid(this.calendar);
        this.updateDateSelection(this.calendar);
        this.setFocusDay(this.calendar, false);
    }

    updateElementLocalization(element, newLocalization) {
        switch (element) {
            case 'ok':
                if (this.okButtonNode && newLocalization) {
                    this.okButtonNode.textContent = newLocalization;
                }
                break;
            case 'cancel':
                if (this.cancelButtonNode && newLocalization) {
                    this.cancelButtonNode.textContent = newLocalization;
                }
                break;
            case 'chooseDate':
                this.buttonLabelChoose = newLocalization || 'Choose Date';
                this.setDateForButtonLabel();
                break;
            case 'changeDate':
                this.buttonLabelChange = newLocalization || 'Change Date';
                this.setDateForButtonLabel();
                break;
            case 'previousYear':
                if (this.calendar.prevYearNode && newLocalization) {
                    this.calendar.prevYearNode.setAttribute(
                        'aria-label',
                        newLocalization
                    );
                }
                break;
            case 'previousMonth':
                if (this.calendar.prevMonthNode && newLocalization) {
                    this.calendar.prevMonthNode.setAttribute(
                        'aria-label',
                        newLocalization
                    );
                }
                break;
            case 'nextMonth':
                if (this.calendar.nextMonthNode && newLocalization) {
                    this.calendar.nextMonthNode.setAttribute(
                        'aria-label',
                        newLocalization
                    );
                }
                break;
            case 'nextYear':
                if (this.calendar.nextYearNode && newLocalization) {
                    this.calendar.nextYearNode.setAttribute(
                        'aria-label',
                        newLocalization
                    );
                }
                break;
        }
    }

    updateLocalization(updatedLocalization) {
        for (const key in updatedLocalization) {
            this.updateElementLocalization(key, updatedLocalization[key]);
        }

        this.localization = { ...this.localization, ...updatedLocalization };
    }
}

export default DatePickerDialog;
